import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import './Dashboard.css'; // Reuse same styles
import { getApiUrl } from '../config/api';
import authFetch from '../utils/authFetch';

const CreateLeasePage = () => {
  const [form, setForm] = useState({
    tenant_first_name: '',
    tenant_last_name: '',
    tenant_email: '',
    property_id: '',
    landlord_name: '',
    security_deposit: '',
    monthly_rent: '',
    rent_due_date: '',
    lease_start_date: '',
    lease_end_date: ''
  });

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_id');
    localStorage.removeItem('user');
    localStorage.removeItem('first_name');
    setIsAuthenticated(false);
    navigate('/login');
  };

  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('access_token');
      if (!token) return navigate('/login');

      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const expTime = payload.exp * 1000;
        if (Date.now() >= expTime) return navigate('/login');
        setIsAuthenticated(true);
      } catch {
        navigate('/login');
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [navigate]);

  useEffect(() => {
    const fetchProperties = async () => {
      setLoading(true);
      try {
        const response = await authFetch(getApiUrl('/api/v1/property/list'));

        if (!response.ok) {
          throw new Error(`Failed to fetch properties: ${response.status}`);
        }

        const data = await response.json();
        setProperties(data.properties || []);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching properties:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for monthlyRent field
    if (name === 'monthly_rent') {
      // Only allow numbers, a single decimal point, and up to 2 decimal places
      const regex = /^(\d+)?(\.\d{0,2})?$/;
      if (value === '' || regex.test(value)) {
        setForm({ ...form, [name]: value });
      }
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Get the user ID from localStorage
    const userId = localStorage.getItem('user_id');
    
    try {
      const response = await authFetch(getApiUrl('/api/v1/lease/testcreate'), {
        method: 'POST',
        body: JSON.stringify({
          ...form,
          user_id: userId
        })
      });

      if (response.ok) {
        // Success - status 2xx
        alert('Lease created and sent for signing!');
        navigate('/leases');
      } else {
        // Error response - get the error message from the API
        const errorData = await response.json();
        const errorMessage = errorData.error || `Failed to create lease (Status: ${response.status})`;
        alert(`Error: ${errorMessage}`);
      }
    } catch (error) {
      // Network or other errors
      console.error('Error creating lease:', error);
      alert('Error: Failed to create lease. Please check your connection and try again.');
    }
  };

  const firstName = localStorage.getItem('first_name') || 'User';

  if (isLoading) return <div className="loading">Loading...</div>;

  return isAuthenticated ? (
    <Layout pageTitle="Create Lease">
      <div className="p-6 max-w-3xl mx-auto space-y-6">
        <h2 className="text-2xl font-semibold">Create Lease Agreement</h2>
        <form
          onSubmit={handleSubmit}
          className="grid gap-4 mt-4 grid-cols-1 sm:grid-cols-2"
        >
          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="tenant_first_name">
              Tenant First Name
            </label>
            <input
              id="tenant_first_name"
              name="tenant_first_name"
              placeholder="First Name"
              className="w-full border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-zenrent-green-500"
              onChange={handleChange}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="tenant_last_name">
              Tenant Last Name
            </label>
            <input
              id="tenant_last_name"
              name="tenant_last_name"
              placeholder="Last Name"
              className="w-full border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-zenrent-green-500"
              onChange={handleChange}
              required
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium mb-1" htmlFor="tenant_email">
              Tenant Email Address
            </label>
            <input
              id="tenant_email"
              name="tenant_email"
              placeholder="Tenant Email"
              className="w-full border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-zenrent-green-500"
              onChange={handleChange}
              required
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium mb-1" htmlFor="property">
              Property
            </label>
            {loading ? (
              <p className="text-sm text-gray-500">Loading properties...</p>
            ) : error ? (
              <p className="text-sm text-red-500">{error}</p>
            ) : properties.length === 0 ? (
              <p className="text-sm text-amber-600">No properties available. Please add a property first.</p>
            ) : (
              <select
                id="property"
                name="property_id"
                className="w-full border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-zenrent-green-500"
                onChange={handleChange}
                required
              >
                <option value="">Select a property</option>
                {properties.map((property, index) => (
                  <option key={index} value={property.id}>
                    {property.name} - {property.address}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="sm:col-span-2">
            <label className="block text-sm font-medium mb-1" htmlFor="landlord_name">
              Landlord / Company Name
            </label>
            <input
              id="landlord_name"
              name="landlord_name"
              placeholder="Your name or rental company name on the lease"
              className="w-full border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-zenrent-green-500"
              onChange={handleChange}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="security_deposit">
              Security Deposit
            </label>
            <input
              id="security_deposit"
              name="security_deposit"
              placeholder="1000.00"
              className="w-full border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-zenrent-green-500"
              onChange={handleChange}
              type="text"
              pattern="^\d+(\.\d{1,2})?$"
              title="Please enter a valid dollar amount (e.g., 1000 or 1000.50)"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="monthly_rent">
              Monthly Rent
            </label>
            <input
              id="monthly_rent"
              name="monthly_rent"
              placeholder="1500.00"
              className="w-full border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-zenrent-green-500"
              onChange={handleChange}
              type="text"
              pattern="^\d+(\.\d{1,2})?$"
              title="Please enter a valid dollar amount (e.g., 1000 or 1000.50)"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="rent_due_date">
              Rent Due Date
            </label>
            <input
              id="rent_due_date"
              type="text"
              name="rent_due_date"
              placeholder="1st"
              className="w-full border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-zenrent-green-500"
              onChange={handleChange}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="lease_start_date">
              Lease Start Date
            </label>
            <input
              id="lease_start_date"
              type="date"
              name="lease_start_date"
              className="w-full border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-zenrent-green-500"
              onChange={handleChange}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="lease_end_date">
              Lease End Date
            </label>
            <input
              id="lease_end_date"
              type="date"
              name="lease_end_date"
              className="w-full border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-zenrent-green-500"
              onChange={handleChange}
              required
            />
          </div>

          <div className="sm:col-span-2">
            <button type="submit" className="action-button w-full">
              Create Lease
            </button>
          </div>
        </form>
      </div>
    </Layout>
  ) : null;
};

export default CreateLeasePage;
